import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react"
import { ItemViewModel, VariantViewModel } from "../../../Types/ExportTypes";
import { CurrencyAutoComplete, ItemGroupAutoComplete, ProgramAutoComplete, CollectionAutoComplete, ColourGroupAutoComplete, SizeGroupAutoComplete, LengthGroupAutoComplete } from "../../AutoComplete/AutoCompletes";
import ImageBox from "../../Image/ImageBox";
import ItemCategoryProductGroupSelect from "../../Item/ItemCategoryProductGroupSelect";
import { Option } from "../../../Types/CommonTypes";
import InfoButton from "../../Tooltip/InfoTextField";
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    FormControl,
    Checkbox,
    FormControlLabel,
    Switch,
    InputAdornment,
    Stepper, Step, StepLabel
} from "@mui/material"
import { useLoading, useAuth, useSnackbar, useNavigation } from "../../../Context/Contexts";
import AsyncAutoCompleteMultiSelect from "../../AsyncAutoCompleteMultiSelect";
import ColorSizeLengthInput from '../../Item/ColorSizeLengthInput';
import LocalizedNumberInputs from "../../Input/LocalizedNumberInputs"

interface Props {
    initialItem: ItemViewModel
    setItemFromWizard: (item: ItemViewModel) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => string | null;
    cancelWizard: () => void;
}

export default function ItemWizard({ initialItem, setItemFromWizard, handleFileChange, cancelWizard }: Props) {
    const { t } = useTranslation();
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { navigate, goBack } = useNavigation();
    const [step, setStep] = useState(0)
    const [item, setItem] = useState<ItemViewModel>(initialItem)
    const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
    const [showAddColour, setShowAddColour] = useState(false);
    const [showAddSize, setShowAddSize] = useState(false);
    const [showAddLength, setShowAddLength] = useState(false);
    const [showMorePrices, setShowMorePrices] = useState(false);
    const [colors, setColors] = useState<Option[]>([]);
    const [sizes, setSizes] = useState<Option[]>([]);
    const [lengths, setLengths] = useState<Option[]>([]);
    const handleNext = () => setStep((s) => s + 1)
    const handleBack = () => setStep((s) => s - 1)

    const handleFinish = async () => {
        setItemFromWizard(item);
    }






    const fetchColorOptions = async (
        search: string,
        offset: number,
        group?: string | null
    ): Promise<Option[]> => {
        const url = group
            ? `/autocompleteApi/Color?group=${group}&search=${search}&offset=${offset}`
            : `/autocompleteApi/Color?search=${search}&offset=${offset}`;
        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    const fetchSizeOptions = async (
        search: string,
        offset: number,
        group?: string | null
    ): Promise<Option[]> => {
        const url = group
            ? `/autocompleteApi/Size?group=${group}&search=${search}&offset=${offset}`
            : `/autocompleteApi/Size?search=${search}&offset=${offset}`;
        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    const fetchLengthOptions = async (
        search: string,
        offset: number,
        group?: string | null
    ): Promise<Option[]> => {
        const url = group
            ? `/autocompleteApi/Length?group=${group}&search=${search}&offset=${offset}`
            : `/autocompleteApi/Length?search=${search}&offset=${offset}`;
        const response = await authService.get<Option[]>(url);
        return response || [];
    };
    const initializeGroups = async () => {
        showLoading();
        try {
            if (colors.length === 0) {
                const colors = await fetchColorOptions("", 0, item.colorGroupCode);
                setColors(colors);
            }

            if (sizes.length === 0) {
                const sizes = await fetchSizeOptions("", 0, item.sizeGroupCode);
                setSizes(sizes);
            }

            if (lengths.length === 0) {
                const lengths = await fetchLengthOptions("", 0, item.lengthGroupCode);
                setLengths(lengths);
            }
         
        } catch (error) {
            console.error("Error initializing groups:", error);
        }
        finally {
            hideLoading();
        }
    };
    const handleChanges = (changes: Partial<ItemViewModel>) => {
        const updatedItem = { ...item, ...changes };
        setItem(updatedItem);
    };

    const handleChange = (key: keyof ItemViewModel, value: any) => {
     
        const updatedItem = { ...item, [key]: value };
        setItem(updatedItem);
    };

    useEffect(() => {
        if (step === 3) {
            initializeGroups();
        }
    }, [step]);

    return (
        <Box sx={{ width: "100%" }}>
            <Stepper activeStep={step} sx={{ mb: 3, overflowX: "auto" }} alternativeLabel>
                <Step><StepLabel>{t("common.itemInfo")}</StepLabel></Step>
                <Step><StepLabel>{t("common.pricing")}</StepLabel></Step>
                <Step><StepLabel>{t("common.variantGroups")}</StepLabel></Step>
                <Step><StepLabel>{t("common.variants")}</StepLabel></Step>
            </Stepper>


            {//item info step
                step === 0 && (
                    <>
                        <Grid container spacing={2}>
                            {/* Editable fields */}

                            <Grid xs={12}>
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    {/* Current or Temporary Image Preview */}
                                    <ImageBox
                                        imageUrl={pendingImageUrl ?? ""}
                                        altText={item.description}
                                        fallbackText="No Image"
                                    />

                                    {/* Image Upload Input */}
                                    <Button
                                        variant="contained"
                                        component="label"
                                        sx={{ textTransform: "none", maxWidth: "250px", mt: 2 }}
                                    >
                                        {t("common.uploadImage")}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={(e) => {
                                                var pendingImageUrl = handleFileChange(e);
                                                setPendingImageUrl(pendingImageUrl)
                                            }}
                                        />
                                    </Button>
                                </Box>
                            </Grid>
                     
                            <Grid xs={12}>
                                <TextField
                                    label={t("common.description")}
                                    value={item.description}
                                    onChange={(e) => handleChange("description", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    margin="normal"
                                    error={!item.description || item.description.trim() === ""}
                                    helperText={
                                        !item.description || item.description.trim() === ""
                                            ? t("help.descriptionRequired")
                                            : ""
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.description")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        
                            <Grid xs={12}>
                                <ItemCategoryProductGroupSelect
                                    key={`${item?.itemCategoryCode ?? ""}::${item?.productGroupCode ?? ""}`}
                                    itemCategoryCode={item.itemCategoryCode}
                                    productGroupCode={item.productGroupCode}
                                    onChange={(cat, group) =>
                                        handleChanges({ itemCategoryCode: cat, productGroupCode: group })
                                    }
                                />
                            </Grid>
                            <Grid xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <ItemGroupAutoComplete
                                        key={item.itemGroupCode || null}
                                        valueId={item.itemGroupCode || null}
                                        onChange={(selected) => handleChange("itemGroupCode", selected?.value || "")}

                                    />
                                </FormControl>
                            </Grid>
                            <Grid xs={12}>
                                <FormControl fullWidth margin="normal">

                                    <ProgramAutoComplete
                                        key={item.programCode || null}
                                        valueId={item.programCode || null}
                                        onChange={(selected) => handleChange("programCode", selected?.value || "")}

                                    />
                                </FormControl>
                            </Grid>
                            <Grid xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CollectionAutoComplete
                                        key={item.collection || null}
                                        valueId={item.collection || null}
                                        onChange={(selected) => handleChange("collection", selected?.value || "")}

                                    />
                                </FormControl>


                            </Grid>
                        </Grid>
                    </>
                )}
            {//pricing
                step === 1 && (<>
                    <Grid container spacing={2}>
                        <Grid xs={12}>
                
                            <LocalizedNumberInputs
                                label={t("common.salesPrice")}
                                value={item.salesPrice || null}
                                onValueChange={(value) => {
                                    handleChange("salesPrice", value ?? null);
                                }}
                                allowNegative={false} // Prevents negative numbers
                                decimalScale={4} // Ensures max 4 decimals
                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                error={!item.salesPrice}
                                helperText={!item.salesPrice ? t("help.salesPriceRequired") : ""}
                            />
                        </Grid>
                        <Grid xs={12}>
                            <FormControl fullWidth margin="normal">
                                <CurrencyAutoComplete
                                    key={item.salesPriceCurrencyCode || null}
                                    valueId={item.salesPriceCurrencyCode || null}
                                    onChange={(selected) => handleChange("salesPriceCurrencyCode", selected?.value || "")}
                                    label={t("common.salesPriceCurrency")}
                                    infoText={t("help.salesPriceCurrency")}
                                    selectFirst={true}

                                />

                            </FormControl>
                        </Grid>
                        <Grid xs={12}>

                            <LocalizedNumberInputs
                                label={t("common.purchasePrice")}
                                value={item.purchasePrice || null}
                                onValueChange={(value) => {
                                    handleChange("purchasePrice", value ?? null);
                                }}
                                allowNegative={false} // Prevents negative numbers
                                decimalScale={4} // Ensures max 4 decimals
                                thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                error={!item.purchasePrice}
                                helperText={!item.purchasePrice ? t("help.purchasePriceRequired") : ""}
                            />
                        </Grid>
                        <Grid xs={12}>
                            <FormControl fullWidth margin="normal">
                                <CurrencyAutoComplete
                                    key={item.purchasePriceCurrencyCode || null}
                                    valueId={item.purchasePriceCurrencyCode || null}
                                    onChange={(selected) => handleChange("purchasePriceCurrencyCode", selected?.value || "")}
                                    label={t("common.purchasePriceCurrency")}
                                    infoText={t("help.purchasePriceCurrency")}
                                    selectFirst={true}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={showMorePrices} onChange={(e) => setShowMorePrices(e.target.checked)} />}
                                label={`${t("common.moreSalesPrices")}`}
                            />
                        </Grid>
                        {showMorePrices && (
                            <>
                                <Grid xs={12}>

                                    <LocalizedNumberInputs
                                        label={t("common.salesPrice") + " 2"}
                                        value={item.salesPrice_2 || null}
                                        onValueChange={(value) => {
                                            handleChange("salesPrice_2", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <FormControl fullWidth margin="normal">
                                        <CurrencyAutoComplete
                                            key={item.salesPriceCurrencyCode_2 || null}
                                            valueId={item.salesPriceCurrencyCode_2 || null}
                                            onChange={(selected) => handleChange("salesPriceCurrencyCode_2", selected?.value || "")}
                                            label={t("common.salesPriceCurrency") +" 2"}
                                            infoText={t("help.salesPriceCurrency")}
                                           

                                        />

                                    </FormControl>
                                </Grid>
                                <Grid xs={12}>

                                    <LocalizedNumberInputs
                                        label={t("common.salesPrice") + " 3"}
                                        value={item.salesPrice_3 || null}
                                        onValueChange={(value) => {
                                            handleChange("salesPrice_3", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <FormControl fullWidth margin="normal">
                                        <CurrencyAutoComplete
                                            key={item.salesPriceCurrencyCode_3 || null}
                                            valueId={item.salesPriceCurrencyCode_3 || null}
                                            onChange={(selected) => handleChange("salesPriceCurrencyCode_3", selected?.value || "")}
                                            label={t("common.salesPriceCurrency") + " 3"}
                                            infoText={t("help.salesPriceCurrency")}
                            

                                        />

                                    </FormControl>
                                </Grid>
                                <Grid xs={12}>

                                    <LocalizedNumberInputs
                                        label={t("common.salesPrice") + " 4"}
                                        value={item.salesPrice_4 || null}
                                        onValueChange={(value) => {
                                            handleChange("salesPrice_4", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <FormControl fullWidth margin="normal">
                                        <CurrencyAutoComplete
                                            key={item.salesPriceCurrencyCode_4 || null}
                                            valueId={item.salesPriceCurrencyCode_4 || null}
                                            onChange={(selected) => handleChange("salesPriceCurrencyCode_4", selected?.value || "")}
                                            label={t("common.salesPriceCurrency") +" 4"}
                                            infoText={t("help.salesPriceCurrency")}
                                        

                                        />

                                    </FormControl>
                                </Grid>
                                <Grid xs={12}>

                                    <LocalizedNumberInputs
                                        label={t("common.salesPrice") +" 5"}
                                        value={item.salesPrice_5 || null}
                                        onValueChange={(value) => {
                                            handleChange("salesPrice_5", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <FormControl fullWidth margin="normal">
                                        <CurrencyAutoComplete
                                            key={item.salesPriceCurrencyCode_5 || null}
                                            valueId={item.salesPriceCurrencyCode_5 || null}
                                            onChange={(selected) => handleChange("salesPriceCurrencyCode_5", selected?.value || "")}
                                            label={t("common.salesPriceCurrency") +" 5"}
                                            infoText={t("help.salesPriceCurrency")}
                                  

                                        />

                                    </FormControl>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </>)}

            {//color,size,lengthGroup
                step === 2 && (<>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <ColourGroupAutoComplete
                                    key={item.colorGroupCode || null}
                                    valueId={item.colorGroupCode || null}
                                    onChange={(selected) => handleChange("colorGroupCode", selected?.value || "")}
                                 

                                />

                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <SizeGroupAutoComplete
                                    key={item.sizeGroupCode || null}
                                    valueId={item.sizeGroupCode || null}
                                    onChange={(selected) => handleChange("sizeGroupCode", selected?.value || "")}


                                />

                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <LengthGroupAutoComplete
                                    key={item.lengthGroupCode || null}
                                    valueId={item.lengthGroupCode || null}
                                    onChange={(selected) => handleChange("lengthGroupCode", selected?.value || "")}


                                />

                            </FormControl>
                        </Grid>
                    </Grid>
                </>
                )}
            {//variant
                step === 3 && (<>
                    <Grid container spacing={2}>
                        {item.colorGroupCode && (
                            <>
                                <Grid item xs={12} lg={12}>
                                    <AsyncAutoCompleteMultiSelect
                                        label={t("common.colour")}
                                        fetchOptions={fetchColorOptions}
                                        initialOptions={colors}
                                        onChange={(selection) => {
                                            handleChange("colors", selection)
                                        }
                                        }
                                        value={item.colors || []}

                                    />

                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAddColour} onChange={(e) => setShowAddColour(e.target.checked)} />}
                                        label={`${t("common.add")} ${t("common.colour")}`}
                                    />
                                </Grid>
                                {showAddColour && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                padding: 2,
                                                borderRadius: 2,
                                                width: "100%"
                                            }}
                                        >
                                            <ColorSizeLengthInput
                                                label={t("common.colour")}
                                                options={colors}
                                                setOptions={setColors}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}

                        {item.sizeGroupCode && (
                            <>
                                <Grid item xs={12} >
                                    <AsyncAutoCompleteMultiSelect
                                        label={t("common.size")}
                                        initialOptions={sizes}
                                        fetchOptions={fetchSizeOptions}
                                        value={item.sizes || []}

                                        onChange={(selection) => {
                                            handleChange("sizes", selection)
                                        }
                                        }

                                    />

                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAddSize} onChange={(e) => setShowAddSize(e.target.checked)} />}
                                        label={`${t("common.add")} ${t("common.size")}`}
                                    />
                                </Grid>
                                {showAddSize && (

                                    <Grid item xs={12} sx={{}}>
                                        <Box
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                padding: 2,
                                                borderRadius: 2,
                                                width: "100%"
                                            }}
                                        >
                                            <ColorSizeLengthInput
                                                label={t("common.size")}
                                                options={sizes}
                                                setOptions={setSizes}
                                            />
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}
                        {item.lengthGroupCode && (
                            <>
                                <Grid item xs={12} >
                                    <AsyncAutoCompleteMultiSelect
                                        label={t("common.length")}
                                        fetchOptions={fetchLengthOptions}
                                        initialOptions={lengths}


                                        onChange={(selection) => {
                                            handleChange("lengths", selection)
                                        }
                                        }
                                        value={item.lengths || []}
                                    />

                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Switch checked={showAddLength} onChange={(e) => setShowAddLength(e.target.checked)} />}
                                        label={`${t("common.add")} ${t("common.length")}`}
                                    />
                                </Grid>
                                {showAddLength && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                backgroundColor: "secondary.main",
                                                padding: 2,
                                                borderRadius: 2,
                                                width: "100%"
                                            }}
                                        >
                                            <ColorSizeLengthInput
                                                label={t("common.length")}
                                                options={lengths}
                                                setOptions={setLengths}
                                            />
                                        </Box>
                                    </Grid>

                                )}
                            </>
                        )}
                    </Grid>
                </>)}

            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                {step < 3 ? (
                    <Button variant="contained" onClick={handleNext}>{t("common.next")}</Button>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleFinish}>{t("common.finish")}</Button>
                )}
                <Button disabled={step === 0} onClick={handleBack}>{t("common.back")}</Button>
                <Button variant="contained" color="warning" onClick={cancelWizard}>
                    {t("common.cancelWizard")}
                </Button>
              
            </Box>
        </Box>
    )
}
